import { format } from 'date-fns'
import { CbzNewContentDocument, CbzLinkFieldsFragment, MediaCbzDocumentFieldsFragment } from '../graphql/generated'
import type { CbzNewContentQuery, CbzNewContentQueryVariables } from '../graphql/generated'
import { Link } from '~/types'
import { useTypedApolloClients } from '~/lib/utils/graphqlDocument'

interface UpdateItem {
  label: string
  title: string
  updatedOn: string
  link: Link
  timestamp: number
  type: 'link' | 'download'
}

const formatDate = (date: string) => {
  const dateObj = new Date(date)
  return format(dateObj, 'dd.MM.yyyy')
}

export const useCbzNewContent = async (days: number = 30, targetGroupTid: string = '0') => {
  const clients = useTypedApolloClients()
  const { t } = useI18n()

  const { data } = await useAsyncData(`cbz-new-content`, async (): Promise<{ items: UpdateItem[] }> => {
    const variables: CbzNewContentQueryVariables = {
      timestamp: Math.floor(Date.now() / 1000 - days * 24 * 60 * 60).toString(),
      targetGroups: [targetGroupTid],
      hasTargetGroup: targetGroupTid !== '0',
    }
    const { data } = await clients!.default.query<CbzNewContentQuery, CbzNewContentQueryVariables>({
      query: CbzNewContentDocument,
      variables,
    })

    const links =
      data?.links?.entities
        ?.filter((item): item is CbzLinkFieldsFragment => item !== null)
        .map((item) => ({
          label:
            item.fieldCbzCategory?.[0]?.entity && 'name' in item.fieldCbzCategory[0].entity
              ? item.fieldCbzCategory[0].entity.name || ''
              : '',
          title: item.title || '',
          updatedOn: `${t('updated on')}: ${formatDate(item.entityChanged || '')}`,
          link: {
            url: item.fieldLink?.url?.path || '',
            target: item.fieldLink?.options?.attributes?.target || '_self',
            text: item.fieldLink?.title || '',
          },
          timestamp: new Date(item.entityChanged || '').getTime(),
          type: 'link' as const,
        })) || []
    const downloads =
      data?.downloads?.entities
        ?.filter((item): item is MediaCbzDocumentFieldsFragment => item !== null)
        .map((item) => ({
          label:
            item.fieldCbzCategory?.[0]?.entity && 'name' in item.fieldCbzCategory[0].entity
              ? item.fieldCbzCategory[0].entity.name || ''
              : '',
          title: item.name || '',
          link: {
            url: item.fieldMediaDocument?.entity?.url || '',
            target: '_blank',
            text: t('download.button'),
          },
          updatedOn: `${t('updated on')}: ${formatDate(item.entityChanged || '')}`,
          timestamp: new Date(item.entityChanged || '').getTime(),
          type: 'download' as const,
        })) || []

    return {
      items: [...links, ...downloads].sort((a, b) => b.timestamp - a.timestamp),
    }
  })

  return data.value ?? { items: [] }
}
